import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  TrendingUp,
  Clock,
  Users,
  DollarSign,
  ChartLine,
  Sparkles,
  Check,
  ArrowRight,
  Calendar,
  MessageSquare,
  Shield,
  Zap,
  Play,
  ChevronRight,
  Utensils,
  BarChart3,
  Calculator,
  Award,
  X,
  Upload,
  AlertCircle,
  Eye,
  Phone,
  HelpCircle,
  UserX,
  Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '@/services/api/apiClient';
import SEOHead from '@/components/SEOHead';
import { DEMO_CONFIG, getDemoImageUrl, DEMO_RESTAURANTS } from '@/config/demo';

const Restaurants = () => {
  // Set SEO-optimized title for Restaurants landing page
  usePageTitle('restaurants');
  
  const [showVideo, setShowVideo] = useState(false);
  const [demoInsights, setDemoInsights] = useState<any>(null);
  const [loadingInsights, setLoadingInsights] = useState(true);

  // Fetch demo insights data
  useEffect(() => {
    const fetchDemoInsights = async () => {
      try {
        const response = await apiClient.get('/public/insights/live-showcase');
        setDemoInsights(response.data);
      } catch (error) {
        console.error('Failed to fetch demo insights:', error);
      } finally {
        setLoadingInsights(false);
      }
    };

    fetchDemoInsights();
  }, []);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Pain points data with animated icons
  const painPoints = [
    {
      icon: Phone,
      title: "Overwhelmed by Inquiries",
      description: "Missing revenue while answering the same questions repeatedly",
      animation: "ring"
    },
    {
      icon: HelpCircle,
      title: "Guessing What Works",
      description: "No clear data on which menu items actually drive profits",
      animation: "spin"
    },
    {
      icon: TrendingUp,
      title: "Labor Costs Rising",
      description: "Can't afford more staff but need better customer service",
      animation: "rise"
    },
    {
      icon: UserX,
      title: "Lost Opportunities",
      description: "Customers leave when they can't get quick answers",
      animation: "fade"
    }
  ];

  // Success metrics
  const successMetrics = [
    {
      restaurant: DEMO_RESTAURANTS.grandTavern.name,
      location: DEMO_RESTAURANTS.grandTavern.location,
      metric: DEMO_RESTAURANTS.grandTavern.metric,
      period: DEMO_RESTAURANTS.grandTavern.period,
      highlight: DEMO_RESTAURANTS.grandTavern.highlight,
      hasVideo: false,
      caseStudyLink: DEMO_RESTAURANTS.grandTavern.caseStudyLink,
      image: DEMO_RESTAURANTS.grandTavern.customImage,
      description: "Automated customer service with intelligent chat assistant"
    },
    {
      restaurant: DEMO_RESTAURANTS.flavorLab.name,
      location: DEMO_RESTAURANTS.flavorLab.location,
      metric: DEMO_RESTAURANTS.flavorLab.metric,
      period: DEMO_RESTAURANTS.flavorLab.period,
      highlight: DEMO_RESTAURANTS.flavorLab.highlight,
      hasVideo: false,
      comingSoon: true,
      image: '/images/60/menu/4259/v3.png',
      description: "Discovered hidden revenue in drink pairings using AI analytics"
    },
    {
      restaurant: DEMO_RESTAURANTS.pizzaPalace.name,
      location: DEMO_RESTAURANTS.pizzaPalace.location,
      metric: DEMO_RESTAURANTS.pizzaPalace.metric,
      period: DEMO_RESTAURANTS.pizzaPalace.period,
      highlight: DEMO_RESTAURANTS.pizzaPalace.highlight,
      hasVideo: false,
      comingSoon: true,
      image: '/images/60/menu/4238/v2.png',
      description: "AI-powered recommendations that customers love"
    }
  ];


  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Restaurant AI Solutions - Dine AI",
    "description": "Join restaurants saving 15+ hours weekly and increasing order values by 32%. AI chat assistant, menu intelligence, and analytics.",
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": "Dine AI for Restaurants",
      "applicationCategory": "BusinessApplication",
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "31.99",
        "highPrice": "79.99",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127"
      }
    }
  };

  return (
    <>
      <SEOHead 
        title="Restaurant AI Solutions | Increase Revenue & Save Labor Costs | Dine AI"
        description="Join restaurants saving 15+ hours weekly and increasing order values by 32%. AI chat assistant, menu intelligence, and analytics. Free 30-day trial."
        keywords="restaurant AI solutions, AI chat for restaurants, menu optimization, restaurant automation, labor cost reduction"
        structuredData={structuredData}
      />
      <div className="min-h-screen">
        <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#2256e5] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 rounded-full bg-dineai-yellow/10 blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 rounded-full bg-white/5 blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center"
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
          >
            {/* Left side - Image */}
            <motion.div 
              className="order-2 md:order-1"
              variants={fadeInUp}
            >
              <div className="backdrop-blur-sm rounded-2xl p-4 shadow-2xl max-w-md mx-auto border border-white/10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
                <img 
                  src="/restaurant-owner-tablet.png" 
                  alt="Restaurant owner using Dine AI" 
                  className="w-full h-auto rounded-xl object-cover"
                />
              </div>
            </motion.div>

            {/* Right side - Content */}
            <div className="order-1 md:order-2">
              <motion.div 
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                variants={fadeInUp}
              >
                <Award className="w-4 h-4 text-dineai-yellow" />
                <span className="text-white/90 text-sm font-medium">Trusted by Restaurants Nationwide</span>
              </motion.div>

              <motion.h1 
                className="text-3xl md:text-4xl font-bold mb-6 text-white"
                variants={fadeInUp}
              >
                Your AI-Powered{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Revenue Engine
                </span>
              </motion.h1>
              
              <motion.p 
                className="text-lg md:text-xl text-white/80 mb-8 max-w-3xl"
                variants={fadeInUp}
              >
                Turn every customer interaction into revenue. Answer questions instantly, discover hidden opportunities, and grow your restaurant with AI that works 24/7.
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4 mb-8"
                variants={fadeInUp}
              >
                <Link to="/restaurant-menu-search">
                  <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2">
                    <Utensils className="w-5 h-5" />
                    START FREE TRIAL
                  </Button>
                </Link>
                <Button 
                  onClick={() => setShowVideo(true)}
                  className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-medium rounded-full transform hover:scale-105 transition-all"
                >
                  WATCH DEMO
                </Button>
              </motion.div>

              <motion.div 
                className="flex flex-col sm:flex-row gap-6 text-white/70"
                variants={fadeInUp}
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-white/60" />
                  <span>Onboard in minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-white/60" />
                  <span>Cancel anytime</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
                <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  THE PROBLEM
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 text-white">
                Your Biggest{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Pain Points
                </span>
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                You're not alone. Every restaurant owner faces these daily challenges.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {painPoints.map((point, index) => (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                  variants={fadeInUp}
                  whileHover={{ y: -5 }}
                >
                  <motion.div 
                    className="w-16 h-16 bg-dineai-yellow rounded-full flex items-center justify-center mb-4 mx-auto"
                    animate={
                      point.animation === "ring" ? {
                        scale: [1, 1.1, 1],
                        rotate: [0, -5, 5, -5, 0]
                      } : point.animation === "spin" ? {
                        rotate: 360
                      } : point.animation === "rise" ? {
                        y: [0, -10, 0]
                      } : {
                        opacity: [1, 0.5, 1]
                      }
                    }
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "loop"
                    }}
                  >
                    <point.icon className="w-8 h-8 text-[#1f4ed1]" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-3 text-white">{point.title}</h3>
                  <p className="text-white/70">{point.description}</p>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
              variants={fadeInUp}
            >
              <Link to="/restaurant-menu-search">
                <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all">
                  BOOST YOUR SALES
                </Button>
              </Link>
              <Link to="/pricing">
                <Button className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-medium rounded-full transform hover:scale-105 transition-all">
                  SEE PRICING PLANS
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Peek Inside Section */}
      <section className="py-20 bg-[#2256e5] relative">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-7xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
                <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  PEEK INSIDE
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Your Command Center for{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Revenue Growth
                </span>
              </h2>
            </motion.div>

            {/* Analytics Preview */}
            <motion.div 
              className="relative"
              variants={fadeInUp}
            >
              {loadingInsights ? (
                <div className="bg-[#3860f4] p-12 rounded-2xl shadow-2xl">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dineai-yellow"></div>
                  </div>
                </div>
              ) : (
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                {/* Actual Analytics Components Preview */}
                <div className="bg-[#3860f4] p-6 space-y-6">
                  {/* Performance Overview */}
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-semibold text-white">Performance Overview</h2>
                      <div className="flex items-center gap-2 text-sm text-white/60">
                        <span>Your AI partner saved you 15 hours this week</span>
                        <Sparkles className="w-4 h-4 text-dineai-yellow" />
                      </div>
                    </div>
                    
                    {/* Metric Cards - Using Flavor Lab Demo Data */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg p-4 border border-green-400/30">
                        <p className="text-3xl font-bold text-white">$4,238</p>
                        <p className="text-white/60 text-sm">Revenue Generated</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg p-4 border border-blue-400/30">
                        <p className="text-3xl font-bold text-white">168</p>
                        <p className="text-white/60 text-sm">Orders Captured</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg p-4 border border-purple-400/30">
                        <p className="text-3xl font-bold text-white">$25.23</p>
                        <p className="text-white/60 text-sm">Avg Order Value</p>
                      </div>
                      <div className="bg-gradient-to-br from-orange-500/20 to-yellow-500/20 rounded-lg p-4 border border-orange-400/30">
                        <p className="text-3xl font-bold text-white">15 hrs</p>
                        <p className="text-white/60 text-sm">Staff Hours Saved</p>
                      </div>
                    </div>
                  </div>

                  {/* This Week's Champions */}
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-yellow-400 rounded-lg">
                        <Sparkles className="h-6 w-6 text-gray-900" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">This Week's Champions</h3>
                        <p className="text-white/70">$2,156 in revenue from top 3</p>
                      </div>
                    </div>
                    
                    {/* Top Items Grid */}
                    <div className="grid md:grid-cols-3 gap-4">
                      {/* Item 1 - Frozen Margarita */}
                      <div className="bg-[#2a4ed8] rounded-xl p-4 relative overflow-hidden">
                        <div className="absolute top-2 left-2 w-8 h-8 bg-dineai-yellow rounded-full flex items-center justify-center z-10">
                          <span className="text-black font-bold text-sm">1</span>
                        </div>
                        <div className="h-32 rounded-lg mb-3 overflow-hidden bg-white/5">
                          <img
                            src="/images/60/menu/4259/v3.png"
                            alt="Frozen Margarita"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                        <h4 className="text-white font-semibold mb-1">Frozen Margarita</h4>
                        <p className="text-2xl font-bold text-emerald-400 mb-2">$12</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-white/60">Orders</p>
                            <p className="text-white font-semibold">89</p>
                          </div>
                          <div>
                            <p className="text-white/60">Revenue</p>
                            <p className="text-emerald-400 font-semibold">$1,068</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Item 2 - Coconut Shrimp */}
                      <div className="bg-[#2a4ed8] rounded-xl p-4 relative overflow-hidden">
                        <div className="absolute top-2 left-2 w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center z-10">
                          <span className="text-white font-bold text-sm">2</span>
                        </div>
                        <div className="h-32 rounded-lg mb-3 overflow-hidden bg-white/5">
                          <img
                            src="/images/60/menu/4238/v2.png"
                            alt="Coconut Shrimp"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                        <h4 className="text-white font-semibold mb-1">Coconut Shrimp</h4>
                        <p className="text-2xl font-bold text-emerald-400 mb-2">$16</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-white/60">Orders</p>
                            <p className="text-white font-semibold">72</p>
                          </div>
                          <div>
                            <p className="text-white/60">Revenue</p>
                            <p className="text-emerald-400 font-semibold">$1,152</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Item 3 - Grilled Mahi Mahi */}
                      <div className="bg-[#2a4ed8] rounded-xl p-4 relative overflow-hidden">
                        <div className="absolute top-2 left-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center z-10">
                          <span className="text-white font-bold text-sm">3</span>
                        </div>
                        <div className="h-32 rounded-lg mb-3 overflow-hidden bg-white/5">
                          <img
                            src="/images/60/menu/4241/v2.png"
                            alt="Grilled Mahi Mahi"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                        <h4 className="text-white font-semibold mb-1">Grilled Mahi Mahi</h4>
                        <p className="text-2xl font-bold text-emerald-400 mb-2">$28</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-white/60">Orders</p>
                            <p className="text-white font-semibold">33</p>
                          </div>
                          <div>
                            <p className="text-white/60">Revenue</p>
                            <p className="text-emerald-400 font-semibold">$924</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AI Menu Intelligence */}
                  <div>
                    <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6">
                      {/* Section Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="inline-block bg-dineai-yellow/20 px-3 py-1.5 rounded-full">
                            <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
                              <Sparkles className="w-4 h-4" />
                              AI MENU INTELLIGENCE
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Weekly Summary */}
                      <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-lg p-4 mb-6 border border-purple-400/30">
                        <div className="flex items-center gap-2 mb-2">
                          <ChartLine className="w-5 h-5 text-purple-400" />
                          <h3 className="text-lg font-semibold text-white">
                            This Week's Intelligence Report
                          </h3>
                        </div>
                        <p className="text-white/80 text-sm">
                          Your tropical-themed items are driving exceptional results! The Frozen Margarita + Coconut Shrimp pairing has generated $198.45 in weekly revenue. Consider creating a "Tropical Escape" combo to maximize this trend.
                        </p>
                      </div>

                      {/* Insight Cards - Vertical Layout */}
                      <div className="grid md:grid-cols-3 gap-4">
                        {/* What's Not Working */}
                        <motion.div 
                          className="bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl p-6 border border-orange-400/30 hover:border-orange-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/20"
                          whileHover={{ y: -5, scale: 1.02 }}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                        >
                          <motion.div 
                            className="flex items-center gap-2 mb-4"
                            whileHover={{ x: 5 }}
                          >
                            <div className="p-2 bg-orange-500/20 rounded-lg">
                              <Target className="w-5 h-5 text-orange-400" />
                            </div>
                            <h4 className="font-semibold text-white">What's Not Working</h4>
                          </motion.div>
                          <div className="space-y-3">
                            <div>
                              <p className="text-white font-medium mb-1">Key Lime Pie</p>
                              <p className="text-white/70 text-sm">Low sales (5/week) despite being signature</p>
                            </div>
                            <div className="pt-3 border-t border-white/10">
                              <p className="text-orange-400 text-sm font-medium">💡 Action:</p>
                              <p className="text-white/80 text-sm">Offer complimentary with $30+ seafood</p>
                            </div>
                          </div>
                        </motion.div>

                        {/* Revenue Opportunities */}
                        <motion.div 
                          className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-6 border border-green-400/30 hover:border-green-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20"
                          whileHover={{ y: -5, scale: 1.02 }}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          <motion.div 
                            className="flex items-center gap-2 mb-4"
                            whileHover={{ x: 5 }}
                          >
                            <div className="p-2 bg-green-500/20 rounded-lg">
                              <TrendingUp className="w-5 h-5 text-green-400" />
                            </div>
                            <h4 className="font-semibold text-white">Revenue Opportunities</h4>
                          </motion.div>
                          <div className="space-y-3">
                            <div>
                              <p className="text-white font-medium mb-1">Margarita + Shrimp Combo</p>
                              <p className="text-white/70 text-sm">Create "Tropical Escape" special</p>
                            </div>
                            <div className="pt-3 border-t border-white/10">
                              <p className="text-green-400 text-sm font-medium">💰 Potential:</p>
                              <p className="text-white/80 text-sm">+$793/month revenue</p>
                            </div>
                          </div>
                        </motion.div>

                        {/* Items to Promote */}
                        <motion.div 
                          className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-6 border border-purple-400/30 hover:border-purple-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
                          whileHover={{ y: -5, scale: 1.02 }}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <motion.div 
                            className="flex items-center gap-2 mb-4"
                            whileHover={{ x: 5 }}
                          >
                            <div className="p-2 bg-purple-500/20 rounded-lg">
                              <Sparkles className="w-5 h-5 text-purple-400" />
                            </div>
                            <h4 className="font-semibold text-white">Items to Promote</h4>
                          </motion.div>
                          <div className="space-y-3">
                            <div>
                              <p className="text-white font-medium mb-1">Grilled Mahi Mahi</p>
                              <p className="text-white/70 text-sm">High-margin ($28) with growing demand</p>
                            </div>
                            <div className="pt-3 border-t border-white/10">
                              <p className="text-purple-400 text-sm font-medium">📢 Strategy:</p>
                              <p className="text-white/80 text-sm">Feature as "Chef's Choice" + wine</p>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </motion.div>

            {/* CTA after showing analytics */}
            <motion.div 
              className="text-center mt-12"
              variants={fadeInUp}
            >
              <h3 className="text-2xl font-bold text-white mb-2">
                Ready to see these results for{" "}
                <span className="text-dineai-yellow">your restaurant</span>?
              </h3>
              <p className="text-white/80 mb-8 text-lg">
                Join the community of restaurants already growing with AI-powered insights
              </p>
              <Link to="/restaurant-menu-search">
                <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-8 sm:px-10 text-lg sm:text-xl font-bold rounded-full shadow-lg transform hover:scale-105 transition-all">
                  START YOUR FREE TRIAL
                  <ArrowRight className="w-6 h-6 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Feature highlights */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <motion.div 
                className="text-center"
                variants={fadeInUp}
              >
                <div className="w-16 h-16 bg-dineai-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-dineai-yellow" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">24/7 Smart Assistant</h3>
                <p className="text-white/70">Answers every question, suggests pairings, handles allergies</p>
              </motion.div>
              
              <motion.div 
                className="text-center"
                variants={fadeInUp}
              >
                <div className="w-16 h-16 bg-dineai-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ChartLine className="w-8 h-8 text-dineai-yellow" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Revenue Intelligence</h3>
                <p className="text-white/70">Weekly insights on what's working and what's not</p>
              </motion.div>
              
              <motion.div 
                className="text-center"
                variants={fadeInUp}
              >
                <div className="w-16 h-16 bg-dineai-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calculator className="w-8 h-8 text-dineai-yellow" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">ROI Calculator</h3>
                <p className="text-white/70">See exactly how much each insight can earn you</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Real Results from{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Real Restaurants
                </span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {successMetrics.map((story, index) => (
                <motion.div
                  key={index}
                  className="group relative bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/20 hover:border-white/40 transition-all duration-300"
                  variants={fadeInUp}
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {/* Image/Video Section */}
                  <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
                    <img 
                      src={story.hasVideo ? story.videoThumbnail : story.image}
                      alt={story.restaurant}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    
                    {/* Play Button for Videos */}
                    {story.hasVideo && (
                      <div className="absolute inset-0 flex items-center justify-center z-20">
                        <motion.div 
                          className="w-16 h-16 bg-dineai-yellow/90 rounded-full flex items-center justify-center cursor-pointer group-hover:bg-dineai-yellow transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Play className="w-8 h-8 text-[#1f4ed1] ml-1" fill="currentColor" />
                        </motion.div>
                      </div>
                    )}
                    
                    {/* Success Badge */}
                    <div className="absolute top-4 left-4 z-20">
                      <div className="bg-dineai-yellow/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-2">
                        <Award className="w-4 h-4 text-[#1f4ed1]" />
                        <span className="text-[#1f4ed1] font-bold text-xs uppercase">Success Story</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Section */}
                  <div className="p-6">
                    {/* Restaurant Info */}
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-white mb-1">{story.restaurant}</h3>
                      <p className="text-white/60 text-sm">{story.location}</p>
                    </div>
                    
                    {/* Key Metric */}
                    <div className="mb-4">
                      <p className="text-4xl font-bold text-dineai-yellow mb-1">{story.metric}</p>
                      <p className="text-white/80 text-sm">{story.period}</p>
                    </div>
                    
                    {/* Description */}
                    <p className="text-white/70 text-sm mb-4">{story.description}</p>
                    
                    {/* CTA */}
                    <div className="pt-4 border-t border-white/10">
                      {story.comingSoon ? (
                        <div className="flex items-center gap-2 text-white/50">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">Video Coming Soon</span>
                        </div>
                      ) : story.caseStudyLink ? (
                        <Link to={story.caseStudyLink} className="flex items-center gap-2 text-dineai-yellow hover:text-white transition-colors">
                          <span className="text-sm font-medium">Read Full Story</span>
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      ) : story.hasVideo ? (
                        <button className="flex items-center gap-2 text-dineai-yellow hover:text-white transition-colors">
                          <Play className="w-4 h-4" />
                          <span className="text-sm font-medium">Watch Video</span>
                        </button>
                      ) : (
                        <button className="flex items-center gap-2 text-dineai-yellow hover:text-white transition-colors">
                          <span className="text-sm font-medium">Read Full Story</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="py-20 bg-[#2256e5]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-5xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                The Fastest Setup in{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  the Industry
                </span>
              </h2>
            </motion.div>

            <div className="space-y-8">
              {[
                {
                  day: "5 Minutes",
                  title: "From PDF to Published",
                  description: "Upload your menu and watch it transform into an AI-powered experience",
                  icon: Upload
                },
                {
                  day: "Instantly",
                  title: "Customers Discover You",
                  description: "Diners download the app, find your restaurant, and get AI-powered recommendations",
                  icon: Users
                },
                {
                  day: "This Sunday",
                  title: "Get Your First Report",
                  description: "Complete weekly analytics delivered every Sunday",
                  icon: ChartLine
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  className="flex gap-6 items-start"
                  variants={fadeInUp}
                >
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-dineai-yellow rounded-full flex items-center justify-center">
                      <step.icon className="w-8 h-8 text-[#1f4ed1]" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-4 mb-2">
                      <span className="text-dineai-yellow font-bold text-lg">{step.day}</span>
                      <h3 className="text-2xl font-bold text-white">{step.title}</h3>
                    </div>
                    <p className="text-white/80 text-lg">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-[#1f4ed1]">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center pt-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Choose the plan that fits your restaurant
            </p>
          </motion.div>
          <PricingTable showToggle={true} defaultBilling="annual" />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#2256e5]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Join the AI-Powered{" "}
              <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                Restaurant Revolution
              </span>
            </h2>
            <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
              Every day you wait is revenue lost. Start your free trial today and see results within a week.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/restaurant-menu-search">
                <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 px-8 text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2">
                  <Utensils className="w-5 h-5" />
                  START FREE TRIAL
                </Button>
              </Link>
              <Button className="bg-transparent border-2 border-white/50 text-white hover:bg-white/10 py-6 px-8 text-lg font-medium rounded-full transform hover:scale-105 transition-all flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                BOOK A DEMO
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-dineai-yellow" />
                <span>Setup in 5 minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-dineai-yellow" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideo && (
          <motion.div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowVideo(false)}
          >
            <motion.div 
              className="relative max-w-4xl w-full bg-black rounded-2xl overflow-hidden"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowVideo(false)}
                className="absolute top-4 right-4 z-10 bg-white/10 backdrop-blur-sm rounded-full p-2 hover:bg-white/20 transition-all"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              <div className="aspect-video bg-gray-900 flex items-center justify-center">
                <Play className="w-16 h-16 text-white/50" />
                <p className="text-white/50 ml-4">Demo Video Placeholder</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
    </>
  );
};

export default Restaurants;